/**
 * 管理端登录页（微信扫码登录，admin-wechat-qr-login:SC-65~SC-70；IKBFM0 UI 收尾轮视觉重设计）。
 *
 * 视觉：开放分栏（左品牌叙事 / 右登录卡 Editorial Split）+ Double-Bezel 白卡 + 静态几何
 * SVG 装饰与内联噪点（自包含无外链）；品牌绿沿用 --admin-* token，微信绿仅作标识语义色。
 *
 * 功能：挂载即取码（POST /auth/wechat-login/qrcode），按后端下发 mode 三态渲染——
 * embed（开放平台）iframe 内嵌微信扫码页（buildWechatEmbedUrl 注入 self_redirect=true，
 * 确认后 iframe 内跳回调，绕开 Firefox 跨域顶级导航拦截）；qrcode（服务号网页授权，
 * 真接主通道）授权 URL 本地渲染二维码图，手机微信扫；mock（fake）渲染演示成员点选。
 * 2s 轮询会话状态，confirmed 建会话跳首页；unregistered 自动刷新；前端 5 分钟过期兜底。
 * 覆盖 admin-web-shell:SC-02 + identity-access:SC-65~SC-70。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, App, Button, Spin, Typography } from 'antd';
import {
  BankOutlined,
  ReloadOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
  WechatOutlined,
} from '@ant-design/icons';
import {
  authApi,
  buildWechatEmbedUrl,
  resolveWechatQrcodeMode,
  type MockLoginStaff,
  type WechatQrcodeMode,
} from '../api/auth.api';
import { ApiError } from '../api/transport';
import { useSession } from '../auth/SessionContext';
import { QrcodeImage } from '../components/QrcodeImage';
import './LoginPage.css';

const { Text } = Typography;

/** 轮询间隔（与后端登录页长轮询节奏对齐）。 */
const POLL_INTERVAL_MS = 2000;
/** 前端过期计时兜底（与后端 sceneToken 5 分钟 TTL 对齐）。 */
const QR_EXPIRE_MS = 5 * 60 * 1000;
/** unregistered 提示停留后自动刷新二维码。 */
const UNREGISTERED_HOLD_MS = 800;

/** 登录页阶段 */
type Phase =
  | 'loading' // 取码中
  | 'qr' // real 模式展示二维码轮询中（embed 内嵌 / qrcode 二维码图）
  | 'mockPick' // fake 模式演示成员点选
  | 'unregistered' // 微信未绑定员工（自动刷新）
  | 'expired' // 二维码过期（手动刷新）
  | 'failed' // 扫码被拒/失败（手动刷新）
  | 'loadError'; // 取码失败（重试）

/** 品牌区特性清单（编辑式编号 + 标题 + 一句话）。 */
const FEATURES = [
  {
    no: '01',
    title: '招生报名',
    desc: '试听、报名、缴费全程留痕，跟进有据可依',
  },
  {
    no: '02',
    title: '教务排课',
    desc: '教师课表、教室资源与课时消耗一目了然',
  },
  {
    no: '03',
    title: '经营看板',
    desc: '收入、课消、续费数据随时可查，决策不靠感觉',
  },
] as const;

/** 右上角静态几何装饰：同心圆环 + 点阵（自包含 SVG，三层品牌绿透明度）。 */
function GeometryDeco() {
  const dots = Array.from({ length: 36 }, (_, i) => {
    const row = Math.floor(i / 9);
    const col = i % 9;
    return <circle key={i} cx={300 + col * 17} cy={40 + row * 17} r="1.6" fill="currentColor" opacity={0.16 + ((row + col) % 4) * 0.06} />;
  });
  return (
    <svg className="login-deco" viewBox="0 0 460 460" aria-hidden="true">
      <circle cx="230" cy="230" r="222" fill="none" stroke="currentColor" strokeOpacity="0.07" strokeWidth="1" />
      <circle cx="230" cy="230" r="168" fill="none" stroke="currentColor" strokeOpacity="0.1" strokeWidth="1" />
      <circle cx="230" cy="230" r="114" fill="none" stroke="currentColor" strokeOpacity="0.14" strokeWidth="1.2" />
      <circle cx="230" cy="230" r="60" fill="none" stroke="currentColor" strokeOpacity="0.1" strokeWidth="1" />
      <circle cx="230" cy="62" r="4.5" fill="currentColor" opacity="0.4" />
      <circle cx="62" cy="230" r="3.5" fill="currentColor" opacity="0.28" />
      <circle cx="398" cy="230" r="3" fill="currentColor" opacity="0.2" />
      {dots}
    </svg>
  );
}

/** 左下角纵深装饰：大号极淡圆环切出画面，与右上几何呼应。 */
function OrbitDeco() {
  return (
    <svg
      className="login-orbit-deco"
      viewBox="0 0 520 520"
      aria-hidden="true"
    >
      <circle cx="90" cy="470" r="300" fill="none" stroke="currentColor" strokeOpacity="0.55" strokeWidth="1" />
      <circle cx="90" cy="470" r="218" fill="none" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1" strokeDasharray="2 7" />
      <circle cx="330" cy="330" r="3" fill="currentColor" opacity="0.4" />
    </svg>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [phase, setPhase] = useState<Phase>('loading');
  const [qrcodeUrl, setQrcodeUrl] = useState('');
  const [qrMode, setQrMode] = useState<WechatQrcodeMode>('mock');
  const [mockStaffs, setMockStaffs] = useState<MockLoginStaff[]>([]);
  const [mockLoadingId, setMockLoadingId] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | undefined>();
  /** 刷新码触发计数（unregistered/手动刷新 +1 重新发起）。 */
  const [attempt, setAttempt] = useState(0);
  const tokenRef = useRef('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const expireRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** 建会话回调经 ref 承载，避免依赖变化重启取码 effect。 */
  const sessionRef = useRef(useSession());
  sessionRef.current = useSession();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const clearTimers = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (expireRef.current) {
      clearTimeout(expireRef.current);
      expireRef.current = null;
    }
    if (refreshRef.current) {
      clearTimeout(refreshRef.current);
      refreshRef.current = null;
    }
  }, []);

  /** confirmed 登录载荷 → 建会话 → 拉权限 → 跳首页（与原密码登录路径一致）。 */
  const establishSession = useCallback(async (loginPayload: {
    token: string;
    staffId: string;
    name: string;
    organizationId: string;
  }) => {
    clearTimers();
    sessionRef.current.login({
      staffId: loginPayload.staffId,
      name: loginPayload.name,
      token: loginPayload.token,
      positions: [],
      organizationId: loginPayload.organizationId,
    });
    // 拉权限快照（含 positions），失败不阻塞登录
    await sessionRef.current.refreshPermissions();
    message.success(`欢迎回来，${loginPayload.name}`);
    navigateRef.current('/', { replace: true });
  }, [clearTimers, message]);

  // 取码 + 启动轮询（attempt 变化即重新发起）
  useEffect(() => {
    clearTimers();
    let cancelled = false;
    setPhase('loading');
    setQrcodeUrl('');
    setErrorCode(undefined);

    void (async () => {
      try {
        const qr = await authApi.getLoginQrcode();
        if (cancelled) return;
        tokenRef.current = qr.sceneToken;
        setQrcodeUrl(qr.qrcodeUrl);
        const mode = resolveWechatQrcodeMode(qr.qrcodeUrl, qr.mode);
        setQrMode(mode);

        if (mode === 'mock') {
          // fake 模式：渲染演示成员点选列表（不轮询二维码）
          setPhase('mockPick');
          try {
            const { items } = await authApi.listMockLoginStaffs();
            if (!cancelled) setMockStaffs(items);
          } catch {
            if (!cancelled) setMockStaffs([]);
          }
          return;
        }

        // real 模式：展示二维码 + 2s 轮询 + 5 分钟过期计时兜底
        setPhase('qr');
        expireRef.current = setTimeout(() => {
          clearTimers();
          setPhase('expired');
        }, QR_EXPIRE_MS);
        pollRef.current = setInterval(async () => {
          try {
            const session = await authApi.pollLoginSession(tokenRef.current);
            if (cancelled) return;
            if (session.status === 'confirmed' && session.login) {
              await establishSession(session.login);
            } else if (session.status === 'unregistered') {
              clearTimers();
              setPhase('unregistered');
              refreshRef.current = setTimeout(() => setAttempt((n) => n + 1), UNREGISTERED_HOLD_MS);
            } else if (session.status === 'expired') {
              clearTimers();
              setPhase('expired');
            } else if (session.status === 'failed') {
              clearTimers();
              setErrorCode(session.errorCode);
              setPhase('failed');
            }
          } catch {
            // 单次轮询网络异常忽略，等待下次重试
          }
        }, POLL_INTERVAL_MS);
      } catch {
        if (!cancelled) setPhase('loadError');
      }
    })();

    return () => {
      cancelled = true;
      clearTimers();
    };
  }, [attempt, clearTimers, establishSession]);

  // 卸载清理（防内存泄漏与登录后残留轮询）
  useEffect(() => clearTimers, [clearTimers]);

  /** fake 模式点选演示成员登录。 */
  const handleMockLogin = async (staffId: string) => {
    setMockLoadingId(staffId);
    try {
      const loginPayload = await authApi.mockLogin(staffId);
      await establishSession(loginPayload);
    } catch (e) {
      const err = e as ApiError;
      message.error(err.message ?? '登录失败');
    } finally {
      setMockLoadingId(null);
    }
  };

  /** 手动刷新二维码/成员列表。 */
  const handleRefresh = () => setAttempt((n) => n + 1);

  const failedText =
    errorCode === 'identity.staff_suspended'
      ? '该员工账号已停用，无法登录'
      : errorCode === 'identity.staff_no_campus_grant'
        ? '该员工无校区授权，无法登录'
        : '扫码登录失败，请刷新二维码重试';

  /** qr 舞台内容：embed 内嵌 iframe / qrcode 二维码图；取码前显示 loading。 */
  const renderQrStageContent = () => {
    if (!qrcodeUrl) {
      return (
        <div className="login-qr-loading" data-testid="login-qr-loading">
          <Spin />
          <span>正在获取二维码…</span>
        </div>
      );
    }
    if (qrMode === 'embed') {
      return (
        <iframe
          data-testid="wechat-login-iframe"
          src={buildWechatEmbedUrl(qrcodeUrl)}
          title="微信扫码登录"
          scrolling="no"
        />
      );
    }
    return <QrcodeImage url={qrcodeUrl} size={232} />;
  };

  /** 舞台遮罩：过期/失败/未注册/取码失败时盖在二维码上，保留上下文并给出动作。 */
  const renderQrOverlay = () => {
    if (phase === 'unregistered') {
      return (
        <div className="login-qr-overlay" data-testid="login-qr-unregistered">
          <Alert
            type="warning"
            showIcon
            message="该微信号未绑定员工"
            description="请先在员工管理中绑定微信，即将自动刷新…"
          />
        </div>
      );
    }
    if (phase === 'expired') {
      return (
        <div className="login-qr-overlay" data-testid="login-qr-expired">
          <Text type="secondary">二维码已过期</Text>
          <Button type="primary" icon={<ReloadOutlined />} onClick={handleRefresh}>
            刷新二维码
          </Button>
        </div>
      );
    }
    if (phase === 'failed') {
      return (
        <div className="login-qr-overlay" data-testid="login-qr-failed">
          <Alert type="error" showIcon message="登录失败" description={failedText} />
          <Button type="primary" onClick={handleRefresh}>
            刷新二维码
          </Button>
        </div>
      );
    }
    if (phase === 'loadError') {
      return (
        <div className="login-qr-overlay" data-testid="login-qr-load-error">
          <Alert type="error" showIcon message="二维码获取失败" description="网络异常或服务不可用，请重试。" />
          <Button type="primary" onClick={handleRefresh}>
            重试
          </Button>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="login-page">
      <GeometryDeco />
      <OrbitDeco />

      <header className="login-topbar">
        <div className="login-brand">
          <div className="login-brand-mark">
            <BankOutlined />
          </div>
          <div>
            <span className="login-brand-name">小象教育</span>
            <span className="login-brand-sub"> · 培训班管理系统</span>
          </div>
        </div>
        <span className="login-topbar-tag">管理端 · 微信安全登录</span>
      </header>

      <main className="login-main">
        <section className="login-hero">
          <span className="login-hero-kicker">教培经营管理平台</span>
          <h1 className="login-hero-title">
            办学的琐碎，
            <br />
            交给<em className="login-hero-mark">系统</em>。
            <span className="login-hero-title-sub">教学的心思，留给学生。</span>
          </h1>
          <p className="login-hero-copy">
            招生报名、教务排课、收费结算、课消对账——小象教育把培训机构的日常经营收进一个后台，
            让每一笔账、每一节课都有据可依。
          </p>
          <ol className="login-features">
            {FEATURES.map((feature) => (
              <li key={feature.no} className="login-feature">
                <span className="login-feature-no">{feature.no}</span>
                <span className="login-feature-title">{feature.title}</span>
                <span className="login-feature-desc">{feature.desc}</span>
              </li>
            ))}
          </ol>
        </section>

        <aside className="login-aside">
          <div className="login-card-shell">
            <div className="login-card">
              <div className="login-card-head">
                <div className="login-wechat-mark">
                  <WechatOutlined />
                </div>
                <h2 className="login-card-title">微信扫码登录</h2>
                <p className="login-card-desc">
                  {qrMode === 'qrcode'
                    ? '用手机微信「扫一扫」扫描二维码，并在手机上确认登录'
                    : '使用微信扫描二维码，在手机上确认登录'}
                </p>
              </div>

              {phase === 'mockPick' ? (
                <div data-testid="login-mock-pick" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                  <span className="login-demo-badge">
                    <span className="login-demo-badge-dot" />
                    演示环境
                  </span>
                  <p className="login-demo-hint">真实微信扫码尚未启用，点选下方成员以该身份直接进入</p>
                  {mockStaffs.length === 0 ? (
                    <div className="login-empty">
                      <span>暂无演示成员</span>
                      <span>请先在员工管理中为员工完成微信绑定，再回到本页登录</span>
                    </div>
                  ) : (
                    <div className="login-member-list">
                      {mockStaffs.map((staff) => (
                        <button
                          key={staff.id}
                          type="button"
                          className={`login-member${mockLoadingId === staff.id ? ' is-loading' : ''}`}
                          disabled={mockLoadingId !== null && mockLoadingId !== staff.id}
                          onClick={() => void handleMockLogin(staff.id)}
                        >
                          <span className="login-member-avatar">{staff.name.slice(0, 1)}</span>
                          <span>
                            <span className="login-member-name">{staff.name}</span>
                            {staff.staffNo ? <div className="login-member-no">{staff.staffNo}</div> : null}
                          </span>
                          <span className="login-member-action">
                            {mockLoadingId === staff.id ? <Spin size="small" /> : <RightOutlined />}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="login-qr-stage">
                    <span className="login-qr-corner login-qr-corner--tl" />
                    <span className="login-qr-corner login-qr-corner--tr" />
                    <span className="login-qr-corner login-qr-corner--bl" />
                    <span className="login-qr-corner login-qr-corner--br" />
                    {renderQrStageContent()}
                    {renderQrOverlay()}
                  </div>

                  <div className="login-qr-hint">
                    <span className="login-qr-hint-step"><span className="login-qr-hint-no">1</span>打开微信</span>
                    <span className="login-qr-hint-arrow">→</span>
                    <span className="login-qr-hint-step"><span className="login-qr-hint-no">2</span>{qrMode === 'qrcode' ? '扫一扫' : '扫描二维码'}</span>
                    <span className="login-qr-hint-arrow">→</span>
                    <span className="login-qr-hint-step"><span className="login-qr-hint-no">3</span>手机上确认</span>
                  </div>

                  <div className="login-security-note">
                    <SafetyCertificateOutlined />
                    微信官方认证登录 · 身份与权限自动识别
                  </div>
                </>
              )}
            </div>
          </div>
        </aside>
      </main>

      <footer className="login-footer">© 2026 小象教育 · 培训班管理系统</footer>
    </div>
  );
}
